import { EVENT } from 'models/event'
import { useEffect, useRef, useCallback, useMemo } from 'react'
import { logger } from 'utils/logger'
import { deepFreeze } from 'utils/deep-freeze'
import { SERVICE_HANDLERS } from './services/service-registry'
import type { PageConfig } from '../../models/pages'

interface IframeElement {
  iframe: HTMLIFrameElement
  origin: string
}

/**
 * TODO: CL-51120: add settings data to check if dedication enabled to checkout URL
 *
 * We need to add settings data to the SDK. To start, this will be needed for dedications.
 * We will need to confirm if all fields are safe, or if we need to filter some data out.
 */
interface UseCustomEmbeddedCodeManagerParams {
  pageConfig: PageConfig
}

/**
 * Manages CustomEmbeddedCode page block iframes by providing a service-oriented
 * communication model between the parent document and sandboxed iframes containing
 * custom code.
 *
 * Manager Pattern Features:
 * - Service method implementations (navigation, cart, etc.)
 * - Event forwarding from page blocks and Whammy features to iframes
 * - Ready signal broadcasting to all managed iframes
 * - Lifecycle management (registration, communication, cleanup)
 *
 * This hook performs side effects only - it does not render any UI.
 *
 * DOM Query Justification:
 * This manager uses direct DOM queries, which is acceptable here because:
 * - We're not modifying the DOM, only reading it (querySelector) and communicating (postMessage)
 * - DOM queries happen in response to events, not during renders
 * - Iframes are static after initial creation
 * - This is a manager hook whose job is managing cross-component communication
 *
 * Future ideas:
 * - Cache iframe DOM elements (i.e. in a Map) instead of querying the DOM every time
 */
export const useCustomEmbeddedCodeManager = ({
  pageConfig,
}: UseCustomEmbeddedCodeManagerParams): void => {
  const hasCustomEmbeddedCode = Boolean(pageConfig?.isCustomEmbeddedCodeEnabled)
  const isPageLoaded = useRef(false)

  const frozenPageConfig = useMemo(() => {
    return deepFreeze(pageConfig)
  }, [pageConfig])

  useEffect(() => {
    if (hasCustomEmbeddedCode) {
      logger('trace', 'CustomEmbeddedCode: Manager instantiated')
    }
  }, [hasCustomEmbeddedCode])

  const getIframes = useCallback((): IframeElement[] => {
    const iframes = document.querySelectorAll(
      'iframe[data-iframe-id]',
    ) as NodeListOf<HTMLIFrameElement>

    const result: IframeElement[] = []

    iframes.forEach((iframe) => {
      // All iframes are sandboxed, therefore the origin is always 'null'
      result.push({ iframe, origin: 'null' })
    })

    logger('trace', 'CustomEmbeddedCode: Found all managed iframes', {
      context: { result },
    })

    return result
  }, [])

  const getIframeById = useCallback((iframeId: string): HTMLIFrameElement | null => {
    const iframe = document.querySelector(
      `iframe[data-iframe-id="${iframeId}"]`,
    ) as HTMLIFrameElement | null

    logger('trace', 'CustomEmbeddedCode: Found iframe by ID', {
      context: { iframeId, iframe },
    })

    return iframe
  }, [])

  /**
   * Broadcast a message to all iframes
   */
  const broadcastToIframes = useCallback(
    (type: string, payload?: unknown) => {
      const iframes = getIframes()

      logger('info', 'CustomEmbeddedCode: Broadcasting message to iframes', {
        context: { type, payload, iframes },
      })

      iframes.forEach(({ iframe }) => {
        const message = {
          type,
          payload,
          source: 'custom-embedded-code-manager',
        }

        try {
          if (!iframe.contentWindow) {
            logger(
              'error',
              'CustomEmbeddedCode: Cannot send message to iframe with no contentWindow',
              {
                context: { message, iframe },
              },
            )
            return
          }

          logger('info', 'CustomEmbeddedCode: Broadcasting message to iframe', {
            context: { message, iframe },
          })

          // FIXME: proper origin handling, CL-51166
          iframe.contentWindow.postMessage(message, '*')
        } catch (error) {
          logger(
            'error',
            new Error('CustomEmbeddedCode: Failed to broadcast message to iframe', {
              cause: error,
            }),
            {
              context: { message, iframe },
            },
          )
        }
      })
    },
    [getIframes],
  )

  /**
   * Send a response to a specific iframe
   */
  const sendResponseToIframe = useCallback(
    (
      iframeId: string,
      messageId: number,
      type: 'RESPONSE_SUCCESS' | 'RESPONSE_ERROR',
      payload: unknown,
    ) => {
      const iframe = getIframeById(iframeId)

      if (!iframe) {
        logger('error', 'CustomEmbeddedCode: Could not find iframe for response', {
          context: { iframeId, messageId, type, payload },
        })
        return
      }

      const message = {
        type,
        payload,
        messageId,
        targetIframeId: iframeId,
        source: 'custom-embedded-code-manager',
      }

      try {
        if (!iframe.contentWindow) {
          logger(
            'error',
            'CustomEmbeddedCode: Cannot send response to iframe with no contentWindow',
            {
              context: { iframeId, message },
            },
          )
          return
        }

        logger('info', 'CustomEmbeddedCode: Sending message to iframe', {
          context: { iframeId, message },
        })

        // FIXME: proper origin handling, CL-51166
        iframe.contentWindow.postMessage(message, '*')
      } catch (error) {
        logger(
          'error',
          new Error('CustomEmbeddedCode: Failed to send response to iframe', { cause: error }),
          {
            context: { iframeId, message },
          },
        )
      }
    },
    [getIframeById],
  )

  /**
   * Send a success response to an iframe, in response to a service method call
   */
  const sendSuccessResponse = useCallback(
    (iframeId: string, messageId: number, payload: unknown) => {
      sendResponseToIframe(iframeId, messageId, 'RESPONSE_SUCCESS', payload)
    },
    [sendResponseToIframe],
  )

  /**
   * Send an error response to an iframe, in response to a service method call
   */
  const sendErrorResponse = useCallback(
    (iframeId: string, messageId: number, message: string) => {
      sendResponseToIframe(iframeId, messageId, 'RESPONSE_ERROR', { message })
    },
    [sendResponseToIframe],
  )

  /**
   * Handle service method calls from iframes.
   * Uses a simple service mapping to delegate calls to individual service modules.
   */
  const handleServiceCall = useCallback(
    async (iframeId: string, messageId: number, type: string, payload: unknown) => {
      try {
        const serviceHandler = SERVICE_HANDLERS[type]

        if (!serviceHandler) {
          logger('warn', 'CustomEmbeddedCode: Unknown service method', {
            context: { iframeId, messageId, type, payload },
          })

          sendErrorResponse(iframeId, messageId, `Unknown method: ${type}`)
          return
        }

        logger('info', `CustomEmbeddedCode: Handling ${type} service call`, {
          context: { campaignId: pageConfig.campaignId, iframeId, messageId, type, payload },
        })

        if (!iframeId || !messageId) {
          logger(
            'error',
            new Error('CustomEmbeddedCode: handleServiceCall missing iframeId and/or messageId'),
            {
              context: { campaignId: pageConfig.campaignId, iframeId, messageId, type, payload },
            },
          )
          return
        }

        await serviceHandler({
          iframeId,
          messageId,
          payload,
          sendSuccessResponse,
          sendErrorResponse,
          pageConfig: frozenPageConfig,
        })
      } catch (error) {
        logger(
          'error',
          new Error(`CustomEmbeddedCode: Error handling service call ${type}`, { cause: error }),
          {
            context: { campaignId: pageConfig.campaignId, iframeId, messageId, type, payload },
          },
        )
        sendErrorResponse(iframeId, messageId, 'Internal service error')
      }
    },
    [pageConfig.campaignId, sendSuccessResponse, sendErrorResponse, frozenPageConfig],
  )

  /**
   * Handle iframe resize requests
   */
  const handleIframeResize = useCallback(
    (iframeId: string, payload: { height?: number }) => {
      try {
        logger('info', 'CustomEmbeddedCode: Handling iframe resize request', {
          context: { iframeId, payload },
        })

        const { height } = payload
        const iframe = getIframeById(iframeId)

        if (!iframe) {
          logger('warn', 'CustomEmbeddedCode: Iframe not found for resize', {
            context: { iframeId },
          })
          return
        }

        iframe.style.height = `${height}px`

        logger('info', 'CustomEmbeddedCode: Iframe resized successfully', {
          context: { iframeId, height },
        })
      } catch (error) {
        logger(
          'error',
          new Error('CustomEmbeddedCode: Error handling iframe resize', { cause: error }),
          {
            context: { iframeId, payload },
          },
        )
      }
    },
    [getIframeById],
  )

  /**
   * Handle messages from iframes.
   * This is the main entry point for all communication with iframes.
   * There is an SDK method that maps to each message type.
   */
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Only process messages from our SDK
      if (!event.data || event.data.source !== 'custom-embedded-code-sdk') {
        logger('info', `CustomEmbeddedCode: Ignoring message - not from SDK`, {
          context: {
            event,
            expectedSource: 'custom-embedded-code-sdk',
          },
        })
        return
      }

      logger('info', 'CustomEmbeddedCode: Processing SDK message', {
        context: { event },
      })

      const { type, payload, messageId, iframeId } = event.data

      // Handle service calls that have a messageId (expecting a response)
      if (messageId && typeof messageId === 'number') {
        handleServiceCall(iframeId, messageId, type, payload)
        return
      }

      // Handle initial resize message
      if (type === EVENT.CUSTOM_EMBEDDED_CODE_RESIZE_IFRAME) {
        handleIframeResize(iframeId, payload)
        return
      }
    },
    [handleServiceCall, handleIframeResize],
  )

  /**
   * Handle page load event.
   * Broadcast ready signal to all iframes.
   */
  const handlePageLoad = useCallback(() => {
    isPageLoaded.current = true
    broadcastToIframes('READY_SIGNAL')

    logger('trace', 'CustomEmbeddedCode: Page loaded, ready signal sent to all iframes')
  }, [broadcastToIframes])

  /**
   * Handle custom events from page blocks and Whammy features.
   * Custom events are forwarded to all iframes.
   *
   * SDK events are prefixed with 'custom-embedded-code' to distinguish them from internal events.
   * The `custom-embedded-code` prefix is removed before forwarding to the iframe.
   *
   * FIXME: since any page block and Whammy feature is allowed to define custom events to broadcast,
   * we need a way to document and version them.
   */
  const handleCustomEvent = useCallback(
    (event: CustomEvent) => {
      const eventName = event.type

      // Check if this is a CustomEmbeddedCode specific event
      if (!eventName.startsWith('custom-embedded-code.')) {
        return
      }

      // Remove the 'custom-embedded-code.' prefix for forwarding
      const forwardedEventName = eventName.replace('custom-embedded-code.', '')

      // Forward the event to all registered iframes, FIXME: should "EVENT" be more explicit?
      broadcastToIframes('EVENT', {
        eventName: forwardedEventName,
        eventPayload: event.detail,
      })

      logger('trace', 'CustomEmbeddedCode: Custom event forwarded to all iframes', {
        context: { event, forwardedEventName },
      })
    },
    [broadcastToIframes],
  )

  /**
   * Set up listeners for postMessage, page load, and custom events
   */
  useEffect(() => {
    if (!hasCustomEmbeddedCode) {
      return
    }

    // Listen for postMessage from iframes
    window.addEventListener('message', handleMessage)

    // Listen for page load
    if (document.readyState === 'complete') {
      handlePageLoad()
    } else {
      window.addEventListener('load', handlePageLoad)
    }

    /**
     * Listen for custom events
     *
     * FIXME: What about a single custom event handler that checks the event type?
     *        That would save us from having to enumerate all events, which will grow over time.
     */
    window.addEventListener(
      EVENT.CUSTOM_EMBEDDED_CODE_CURRENCY_CHANGE,
      handleCustomEvent as EventListener,
    )

    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('load', handlePageLoad)
      window.removeEventListener(
        EVENT.CUSTOM_EMBEDDED_CODE_CURRENCY_CHANGE,
        handleCustomEvent as EventListener,
      )
    }
  }, [hasCustomEmbeddedCode, handleMessage, handlePageLoad, handleCustomEvent])
}
